import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { WebSocketNotificationType, WebSocketTaskNotification } from 'src/app/core/websocket/model';
import { WebSocketService } from 'src/app/core/websocket/websocket.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { TaskStateInWsNotif } from 'src/app/shared/models/misc.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import GenericUtils from 'src/app/shared/utils/generic.utils';
import { Task } from 'src/app/tasks/shared/models';

import { CountersService } from '../../inbox/services/counters.service';
import { QueueIdEnum } from '../../inbox/shared/enums';
import { Counter, TaskListConfiguration } from '../../inbox/shared/models';
import { TaskActionsService } from '../../inbox/task-actions/task-actions.service';
import { PushboxService } from '../services/pushbox.service';
import { PushboxStatesEnum } from '../shared/enums';

@Component({
  selector: 'app-pushbox',
  templateUrl: './pushbox.component.html',
  styleUrls: ['./pushbox.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PushboxComponent implements OnInit, OnDestroy {
  @Input() taskListConfiguration: TaskListConfiguration;

  parentContainer: HTMLElement;
  parentContainerId = 'right-side-content';
  ngPushboxClass = 'ng-pushbox';

  counters: Counter[] = [];

  private welcomeEmojiList = ['😀', '😄', '😉', '😊', '🙂'];
  private continueEmojiList = ['😎', '🤩', '👏', '👍', '💪', '👌', '🎖', '🏅', '🥇', '🏆', '🎯', '🚀', '🎊', '🎉', '✅'];
  welcomeEmoji = '';
  continueEmoji = '';

  highestPriorityTask: Task = null;

  private continueInterval: any;
  continueMode = {
    active: false,
    since: 0,
    minutes: 0,
  };

  private subscriptions = new Subscription();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private countersService: CountersService,
    private dialog: MatDialog,
    private pushboxService: PushboxService,
    private renderer: Renderer2,
    private taskActionsService: TaskActionsService,
    private webSocketService: WebSocketService,
    @Inject(DOCUMENT) public document: Document,
    @Inject('LoginFactory') private ajsLoginService: any,
    @Inject('$state') private $state: any,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {
    this.parentContainer = this.document.getElementById(this.parentContainerId);
  }

  ngOnInit() {
    this.renderer.addClass(this.parentContainer, this.ngPushboxClass);
    this.welcomeEmoji = this.welcomeEmojiList[GenericUtils.getRandomInt(0, this.welcomeEmojiList.length - 1)];
    this.continueEmoji = this.continueEmojiList[GenericUtils.getRandomInt(0, this.continueEmojiList.length - 1)];

    this.subscriptions.add(
      this.countersService.countersBS.subscribe(counters => {
        this.updateCountersForDisplay(counters);
      })
    );

    this.countersService.getAllCounters(this.taskListConfiguration).subscribe();

    this.updateToolbarCounters();

    this.ajsLoginService.setConfirmLogoutCallback(() => this.confirmLogout());

    // Task notification
    this.subscriptions.add(
      this.webSocketService.message$
        .pipe(filter((event: any) => event.type === WebSocketNotificationType.Task))
        .subscribe({
          next: (taskEvent: WebSocketTaskNotification) => {
            this.countersService.updateCounters(taskEvent);

            // enable continue mode + chrono if the user has really made an action on a task
            const statesToWatch = [TaskStateInWsNotif.Answered, TaskStateInWsNotif.Archived, TaskStateInWsNotif.OnHold];
            if (
              taskEvent.user_id === this.alcmeonContextService.self.id &&
              statesToWatch.includes(taskEvent.task_state)
            ) {
              this.updateToolbarCounters();
              this.resetContinueMode();
            }
          },
        })
    );
  }

  acquireAndProcessNextTask() {
    this.basicService.showSpinner();
    this.acquireAndProcessNextTaskWithRetry(3);
  }

  /**
   * Retreives the next task, tries to acquire it, and retries the given number of times if someone else grabbed the task faster than us
   * If acquisition is successful, go to the .process state to process the task.
   */
  private acquireAndProcessNextTaskWithRetry(retries: number) {
    if (retries > 0) {
      // do not hide spinner in finalize(), because otherwise, when everything is ok, it would be hidden here and appear again a few sec later when loading PP
      this.getNextTask().subscribe({
        next: task => {
          if (task) {
            // If the task is already assigned to us (we can't get a task assigned to someone else), then just go to process state
            if (task.state === TaskStateInWsNotif.Assigned) {
              // absolute state, because of sentry error 313690
              this.$state.go(PushboxStatesEnum.Process, { processedTaskId: task.id });
            }
            // try to acquire the task
            else {
              this.taskActionsService.assign(task.id, task.state).subscribe({
                next: response => {
                  if (this.taskActionsService.wasAcquiredByMe(response, task.id, this.alcmeonContextService.self)) {
                    // acquisition succesful
                    this.$state.go(PushboxStatesEnum.Process, { processedTaskId: task.id });
                  } else {
                    // let's retry if an other user grabbed the next task quicker than us
                    this.acquireAndProcessNextTaskWithRetry(retries - 1);
                  }
                },
                error: () => {
                  // let's retry if something went wrong
                  this.acquireAndProcessNextTaskWithRetry(retries - 1);
                },
              });
            }
          } else {
            // no task available, user has been alerted by getNextTask() below
            this.basicService.hideSpinner();
          }
        },
        error: () => {
          this.basicService.hideSpinner();
        },
      });
    } else {
      this.basicService.hideSpinner();
      this.basicService.toastAlert('pushbox/error/acquisition-max-retry');
    }
  }

  private getNextTask(): Observable<Task> {
    return this.pushboxService.getHighestPriorityTask().pipe(
      map(response => {
        if (response.status === 204) {
          this.basicService.toastInformation('pushbox/error/no-pending-message');
          return null;
        } else {
          return response.body;
        }
      })
    );
  }

  private updateToolbarCounters() {
    this.pushboxService.getTodayCounters().subscribe(todayCounters => {
      this.topToolbarService.setTppTodayCounters(
        todayCounters.answered,
        todayCounters.archived,
        todayCounters.assigned,
        null
      );
    });
  }

  private resetContinueMode() {
    this.continueMode.active = true;
    this.continueMode.since = Date.now();
    this.updateContinueChrono();

    clearInterval(this.continueInterval);
    this.continueInterval = setInterval(() => this.updateContinueChrono(), 60 * 1000);
  }

  private updateContinueChrono() {
    const delta = Math.floor((Date.now() - this.continueMode.since) / 1000);
    this.continueMode.minutes = Math.floor(delta / 60);
  }

  goToOngoingByMe() {
    this.$state.go(
      PushboxStatesEnum.Tasks,
      {
        queueId: QueueIdEnum.AssignedToYou,
      },
      {
        reload: PushboxStatesEnum.Tasks,
      }
    );
  }

  logout() {
    const company = this.alcmeonContextService.getCompany();
    this.ajsLoginService.logout(company);
  }

  private async confirmLogout(): Promise<boolean> {
    if (this.taskListConfiguration.counter && this.taskListConfiguration.counter[0]) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: ['medium-size'],
        disableClose: true,
        data: {
          title: this.basicService.translate('pushbox/logout_warning/title'),
          description: this.basicService.translate('pushbox/logout_warning/ongoing-text'),
          cancelBtnText: this.basicService.translate('pushbox/logout_warning/confirm_logout'),
          confirmBtnText: this.basicService.translate('pushbox/logout_warning/ongoing-cancel-button'),
        },
      });

      return dialogRef
        .afterClosed()
        .toPromise()
        .then(confirm => {
          if (confirm) {
            this.goToOngoingByMe();
          }
          // confirm button is inverted in UI, to have the "cancel" action in blue
          return !confirm;
        });
    } else {
      return Promise.resolve(true);
    }
  }

  /**
   * Inject counters in buttonsTaskListConfigs
   */
  private updateCountersForDisplay(counters: Counter[]) {
    if (counters?.length) {
      this.counters = counters;
      const { counterIndex } = this.taskListConfiguration;

      if (this.counters[counterIndex]) {
        this.taskListConfiguration.counter = [this.counters[counterIndex].counter];
      }
    }
  }

  ngOnDestroy() {
    this.renderer.removeClass(this.parentContainer, this.ngPushboxClass);
    this.ajsLoginService.setConfirmLogoutCallback(null);
    this.topToolbarService.clearTppTodayCounters(null);
    this.subscriptions.unsubscribe();
    clearInterval(this.continueInterval);
  }
}
